<template>
  <div class="w-full h-full">
    <AlertDialog :open="showFormModal" @update:open="(o) => !o && (showFormModal = false)">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Edit timeframe</AlertDialogTitle>
          <AlertDialogDescription>Edit analytics timeframe</AlertDialogDescription>
        </AlertDialogHeader>
        <div class="space-y-3">
          <div>
            <label class="text-sm font-medium" for="form-start">Start date</label>
            <Input id="form-start" v-model="formStart" type="date" />
          </div>
          <div>
            <label class="text-sm font-medium" for="form-end">End date</label>
            <Input id="form-end" v-model="formEnd" type="date" />
          </div>
        </div>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="!(formStart != '' && formEnd != '')"
            @click="updateAnalytics(); showFormModal = false"
          >Apply</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <div class="flex items-center gap-3 mb-4">
      <Telescope class="w-7 h-7 text-foreground" />
      <h1 class="text-2xl font-semibold tracking-tight">Spend explorer - {{ selectedCategory }}</h1>
    </div>

    <Breadcrumb class="mb-4">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink as-child><router-link :to="`/spend-explorer?start=${formStart}&end=${formEnd}`">spend-explorer</router-link></BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink as-child><router-link :to="`/spend-explorer/${$route.params.category}?start=${formStart}&end=${formEnd}`">{{ $route.params.category }}</router-link></BreadcrumbLink>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>

    <div v-if="!finishedloading" class="space-y-4">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Skeleton v-for="i in 4" :key="`s${i}`" class="h-20" />
      </div>
      <div class="space-y-2">
        <Skeleton v-for="i in 5" :key="`t${i}`" class="h-10" />
      </div>
    </div>

    <section v-if="finishedloading" class="mb-4">
      <div class="flex items-baseline justify-between mb-3">
        <h2 class="text-lg font-semibold">{{ selectedCategory }} - Spend Analysis</h2>
        <div>
          <Button size="sm" id="createContract" @click="showFormModal = true">
            <CalendarDays class="w-4 h-4" />Edit timeframe
          </Button>
        </div>
      </div>
      <div v-if="analytics != undefined && retailers.length > 0" class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Pecentage share of wallet</p>
            <Percent class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ fmt((analytics.categorySpend.totalTransactionVolume / analytics.totalSpend.totalTransactionVolume) * 100, { decimals: 2, percentage: true }) }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Spend</p>
            <CreditCard class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ fmt(Math.abs(analytics.categorySpend.totalTransactionVolume), { decimals: 2, currency: '£' }) }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Transactions</p>
            <CreditCard class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ fmt(analytics.categorySpend.transactionCount, { decimals: 0 }) }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Top retailer by spend</p>
            <Store class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ retailers[0].retailer.retailerName }}</p>
        </Card>
      </div>
    </section>

    <Card v-if="finishedloading">
      <CardHeader>
        <CardTitle>Top retailers</CardTitle>
        <CardDescription>View the top retailers your customer spends with between {{ niceDateTime(startDate) }} to {{ niceDateTime(endDate) }}</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="retailers"
          :loading="!finishedloading"
          :error="error"
          @retry="getRetailers(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          :hide-footer="true"
        >
          <template #item-retailer="retailers">
            <div class="flex items-center gap-2">
              <img v-if="retailers.retailer.logo" :src="retailers.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
              <span class="text-sm font-medium truncate">{{ retailers.retailer.retailerName }}</span>
            </div>
          </template>
          <template #item-transactionVolume="{ transactionVolume }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Math.abs(transactionVolume).toFixed(2) }}</span>
          </template>
          <template #item-transactions="{ transactionCount }">
            <div class="text-right tabular-nums">{{ fmt(Math.abs(transactionCount), { decimals: 0 }) }}</div>
          </template>
          <template #item-spend="{ transactionVolume }">
            <div class="text-right tabular-nums">{{ fmt(Math.abs(transactionVolume), { decimals: 2, currency: '£' }) }}</div>
          </template>
          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                @click="
                  $router.push(
                    `/retailer/${retailers.retailer.id}?start=${formStart}&end=${formEnd}`
                  )
                "
              >View retailer</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Skeleton,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  Input,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@boogi/ui'
import { Telescope, CalendarDays, Percent, CreditCard, Store } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Skeleton,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Input,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbSeparator,
    Telescope,
    CalendarDays,
    Percent,
    CreditCard,
    Store,
  },
  props: {},

  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: 50,
      },
      selectedCategory: this.$route.params.category
        .replaceAll('-', ' ')
        .replace(/^\s*([a-z])/i, (match, firstLetter) => firstLetter.toUpperCase()),

      analytics: undefined,

      serverItemsLength: undefined,
      startDate: '',
      endDate: '',
      formStart: '',
      formEnd: '',
      category: '',

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      loadingSearch: false,
      loadingAnalytics: true,
      loadingRetailers: true,
      headersTable1: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Category', value: 'retailer.personalisationCategories' },
        { text: 'Transactions', value: 'transactions', align: 'right' },
        { text: 'Spend', value: 'spend', align: 'right' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
    }
  },
  created() {
    if (this.$route.query.start && this.$route.query.end) {
      this.formStart = this.$route.query.start
      this.formEnd = this.$route.query.end
      this.startDate = DateTime.fromFormat(this.formStart, 'dd-mm-yyyy').startOf('day')
      this.endDate = DateTime.fromFormat(this.formEnd, 'dd-mm-yyyy').endOf('day')
    } else {
      const now = DateTime.local()
      const startOfMonth = now.startOf('month')
      const endOfMonth = now.endOf('month')

      this.startDate = startOfMonth.minus({ month: 2 })
      this.endDate = endOfMonth
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
    }
    this.getRetailers(0, 50)
    this.getAnalytics()
  },
  methods: {
    fmt(n, opts = {}) {
      const { decimals = 2, currency = '', percentage = false } = opts
      const num = Number(n) || 0
      const formatted = num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      return `${currency}${formatted}${percentage ? '%' : ''}`
    },
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_SHORT)
    },

    updateAnalytics() {
      this.startDate = DateTime.fromISO(this.formStart).startOf('day')
      this.endDate = DateTime.fromISO(this.formEnd).endOf('day')
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
      this.getAnalytics()
      this.getRetailers(0, 50)
    },

    async getAnalytics() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loadingAnalytics = true
      this.error = false
      var category = this.$route.params.category

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/share-of-wallet/category/${category}?startDate=${this.formStart}&endDate=${this.formEnd}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.analytics = object
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingAnalytics = false
      }
    },

    async getRetailers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loadingRetailers = true
      this.error = false

      var category = this.selectedCategory.replaceAll(' ', '%20')
      if (category == 'Food%20and%20drink') {
        category = 'Food%20and%20Drink'
      }
      if (category == 'Saas%20tools') {
        category = 'SaaS%20tools'
      }

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/share-of-wallet/retailer/popularity?page=${page}&size=${size}&startDate=${this.formStart}&endDate=${this.formEnd}${category == '' ? '' : '&categories=' + category}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.length
          this.retailers = object
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingRetailers = false
      }
    },
  },
  computed: {
    finishedloading() {
      return this.loadingAnalytics == false && this.loadingRetailers == false
    },
  },
  mounted() {
    document.title = 'Dashboard | Spend explorer'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getRetailers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
